import React from "react";
import OpcLockTags from "../../_shared/OpcLockTags/OpcLockTags";
import { reviewFact, statusMeta } from "../../_config/okrPlansCoachConfig";
import {
  day,
  initialsOf,
  plural,
  quarterName,
  quarterRange,
  toDate,
  toIsoDay,
} from "../../_config/okrPlansCoachFormat";
import "./_plan-head.scss";

// ═══════════════════════════════════════════════════════════════════════════
// Plan header (CD7, CD17, CD24): eyebrow (company · quarter), objective,
// owner + linked company objective, status pill (read-only), after-lock
// markers; four quiet fact tiles — Progress · Review · Quarter week ·
// Check-ins due (only the review tile changes colour).
// ═══════════════════════════════════════════════════════════════════════════

const STATUS_CLASS = {
  ok: "is-ok",
  warn: "is-warn",
  late: "is-late",
  muted: "is-muted",
};

const PlanHead = ({ plan, companyName }) => {
  const review = reviewFact(plan);
  const status = statusMeta(plan.objective?.status);
  const end = toDate(plan.quarter?.end_date);
  const start = end ? new Date(end.getFullYear(), end.getMonth() - 2, 1) : null;
  const q = {
    start_date: start && toIsoDay(start),
    end_date: plan.quarter?.end_date,
  };
  const name = end ? quarterName(q) : plan.quarter?.label;
  const range = end ? quarterRange(q) : "";
  const progress = plan.objective?.progress ?? 0;
  const owner = plan.owner || {};
  return (
    <div className="opc-pv-head">
      <span className="opc-pv-eyebrow">
        {[companyName, name?.startsWith("Q") ? name : null, range]
          .filter(Boolean)
          .join(" · ")}
      </span>
      <h1 className="opc-pv-title">{plan.objective?.text}</h1>
      <div className="opc-pv-meta">
        <span className="opc-pv-avatar">{initialsOf(owner.name)}</span>
        <span className="dark-grey">{owner.name}</span>
        {owner.role ? <span className="light-text">{owner.role}</span> : null}
        {owner.is_active === false ? (
          <span className="opc-tag">Deactivated</span>
        ) : null}
        {plan.company_objective ? (
          <>
            <span className="opc-pv-sep" />
            {/* CD41: the same link icon as the SME's link chip. */}
            <i
              className="fa-solid fa-link light-text opc-pv-link-icon"
              aria-hidden="true"
            />
            <span className="light-text">
              {plan.company_objective.code} · {plan.company_objective.text}
            </span>
          </>
        ) : null}
        <span className="opc-pv-sep" />
        <span
          className={`opc-pc__note opc-head-status ${STATUS_CLASS[status.tone] || ""}`}
        >
          {status.label}
        </span>
        <OpcLockTags flags={plan.flags} />
      </div>
      {plan.is_archived ? (
        <p className="light-text small mt-2 mb-0">
          Objective deleted on {day(plan.objective_deleted_at)} — this plan is
          read-only.
        </p>
      ) : null}

      <div className="opc-pv-facts">
        <div className="opc-pv-fact">
          <span className="opc-pv-fact__label">Progress</span>
          <span className="opc-pv-fact__value">{progress}%</span>
          <span className="opc-pv-fact__bar">
            <i style={{ width: `${progress}%` }} />
          </span>
        </div>
        <div
          className={`opc-pv-fact${review.tone ? ` is-${review.tone}` : ""}`}
        >
          <span className="opc-pv-fact__label">Review</span>
          <span className="opc-pv-fact__value">{review.value}</span>
          <span className="opc-pv-fact__caption">{review.caption}</span>
        </div>
        <div className="opc-pv-fact">
          <span className="opc-pv-fact__label">Quarter week</span>
          <span className="opc-pv-fact__value">
            {plan.quarter?.week_number ?? "—"}{" "}
            <small>of {plan.quarter?.total_weeks ?? 13}</small>
          </span>
          <span className="opc-pv-fact__caption">
            Started {day(plan.started_at)}
          </span>
        </div>
        <div className="opc-pv-fact">
          <span className="opc-pv-fact__label">Check-ins due</span>
          <span className="opc-pv-fact__value">
            {plan.checkins_due || "None"}
          </span>
          <span className="opc-pv-fact__caption">
            {plural(plan.reports_count || 0, "weekly report")}
          </span>
        </div>
      </div>
    </div>
  );
};

export default PlanHead;
